import {
	type BalanceSheetId,
	balanceSheetSectionPolicy,
	Currency,
	type Dtos,
	FinancialPeriod,
	Formula,
	normalizeFormulaForSection,
	type SuretyAccountId,
} from "@inrev/common";
import { Option } from "@inrev/core";
import {
	useCreateBalanceSheetLineItem,
	useDeleteBalanceSheetLineItems,
	useRenameBalanceSheetLineItem,
	useUpdateBalanceSheet,
} from "@portal/api/financials";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { type FC, useCallback, useMemo } from "react";
import { FinancialDataGrid } from "../ui/FinancialDataGrid/FinancialDataGrid";
import type { NormalizeAdjustment } from "../ui/FinancialDataGrid/types";
import {
	ADMIN_VIEW_OPTIONS,
	type AdjustmentOverrideArgs,
	type CreateRowArgs,
	type DeleteRowArgs,
	type GivenOverrideArgs,
	type PeriodViewModel,
	READ_ONLY_VIEW_OPTIONS,
	type RenameRowArgs,
	type StatementMatrixViewModel,
	ViewModeEnum,
} from "../ui/FinancialDataGrid/types";
import { collectExpandableRowIds } from "../ui/FinancialDataGrid/utils/collectExpandableRowIds";
import { BalanceSheetPeriodHeaderContextMenu } from "./BalanceSheetPeriodHeaderContextMenu";
import { useFinancialsPeriodSelector } from "./FinancialsPeriodSelectorProvider";
import { filterStatementViewModelPeriods } from "./utils/filterStatementViewModelPeriods";
import { mergeBalanceSheetWithEdits } from "./utils/mergeBalanceSheetWithEdits";

type BalanceSheetGridProps = {
	accountId: SuretyAccountId;
	baseViewModel: StatementMatrixViewModel<BalanceSheetId>;
};

function parseRowId(
	rowId: string,
): { sectionPath: FinancialPeriod.BalanceSheetLineItemPath; label: string } | null {
	if (!rowId.startsWith("bs::")) return null;
	const marker = "::group::";
	const markerIdx = rowId.indexOf(marker);
	if (markerIdx === -1) return null;
	const label = rowId.slice(markerIdx + marker.length);
	const sectionPath = rowId
		.slice("bs::".length, markerIdx)
		.split("::")
		.filter((p) => p !== "main")
		.join(".");
	if (!FinancialPeriod.isBalanceSheetLineItemPath(sectionPath)) {
		return null;
	}
	return { sectionPath, label };
}

function parseGroupRowId(rowId: string): FinancialPeriod.BalanceSheetLineItemPath | null {
	const suffix = "::group";
	if (!rowId.startsWith("bs::") || !rowId.endsWith(suffix)) return null;
	const sectionPath = rowId
		.slice("bs::".length, rowId.length - suffix.length)
		.split("::")
		.filter((p) => p !== "main")
		.join(".");
	return FinancialPeriod.isBalanceSheetLineItemPath(sectionPath) ? sectionPath : null;
}

export const BalanceSheetGrid: FC<BalanceSheetGridProps> = ({ accountId, baseViewModel }) => {
	const { isAdmin } = useAuthenticatedUser();
	const { isBalanceSheetSelected } = useFinancialsPeriodSelector();
	const { updateBalanceSheet } = useUpdateBalanceSheet(accountId);
	const { deleteBalanceSheetLineItems } = useDeleteBalanceSheetLineItems(accountId);
	const { createBalanceSheetLineItem } = useCreateBalanceSheetLineItem(accountId);
	const { renameBalanceSheetLineItem } = useRenameBalanceSheetLineItem(accountId);

	const filteredViewModel = useMemo(
		() => filterStatementViewModelPeriods(baseViewModel, isBalanceSheetSelected),
		[baseViewModel, isBalanceSheetSelected],
	);
	const expandableRowIds = useMemo(
		() => collectExpandableRowIds(filteredViewModel.rows),
		[filteredViewModel.rows],
	);

	const renderPeriodContextMenu = useCallback(
		(period: PeriodViewModel<BalanceSheetId>) => (
			<BalanceSheetPeriodHeaderContextMenu
				accountId={accountId}
				balanceSheetData={{
					id: period.id,
					fileId: period.fileId,
					reviewedAt: period.reviewedAt,
					periodEndDate: period.periodEndDate,
					periodType: period.periodType,
					preparationMethod: period.preparationMethod,
				}}
				isActive={period.isActive}
			/>
		),
		[accountId],
	);

	const handleGivenOverride = useCallback(
		async ({ rowId, periodId, value }: GivenOverrideArgs<BalanceSheetId>) => {
			const parsed = parseRowId(rowId);
			if (!parsed) {
				throw new Error("Cannot save changes for this line item");
			}
			const values = value === null ? { value: null } : { value: Currency.round(value) };
			const body: Dtos.Admin.BalanceSheet.Update.Request = {
				sectionPath: parsed.sectionPath,
				label: parsed.label,
				values,
			};
			await updateBalanceSheet({ balanceSheetId: periodId, body });
		},
		[updateBalanceSheet],
	);

	const handleAdjustmentOverride = useCallback(
		async ({ rowId, periodId, adjustment }: AdjustmentOverrideArgs<BalanceSheetId>) => {
			const parsed = parseRowId(rowId);
			if (!parsed) {
				throw new Error("Cannot save changes for this line item");
			}
			const body: Dtos.Admin.BalanceSheet.Update.Request = {
				sectionPath: parsed.sectionPath,
				label: parsed.label,
				values: adjustment === null ? { formula: null } : { formula: adjustment },
			};
			await updateBalanceSheet({ balanceSheetId: periodId, body });
		},
		[updateBalanceSheet],
	);

	const handleDeleteRow = useCallback(
		async ({ rowId }: DeleteRowArgs) => {
			const parsed = parseRowId(rowId);
			if (!parsed) {
				throw new Error("Cannot delete this line item");
			}
			await deleteBalanceSheetLineItems({
				sectionPath: parsed.sectionPath,
				label: parsed.label,
				balanceSheetIds: filteredViewModel.periods.map((p) => p.id),
			});
		},
		[deleteBalanceSheetLineItems, filteredViewModel.periods],
	);

	const handleRenameRow = useCallback(
		async ({ rowId, newLabel }: RenameRowArgs) => {
			const parsed = parseRowId(rowId);
			if (!parsed) {
				throw new Error("Cannot rename this line item");
			}
			await renameBalanceSheetLineItem({
				sectionPath: parsed.sectionPath,
				oldLabel: parsed.label,
				newLabel,
				balanceSheetIds: filteredViewModel.periods.map((p) => p.id),
			});
		},
		[renameBalanceSheetLineItem, filteredViewModel.periods],
	);

	const handleCreateRow = useCallback(
		async ({
			parentRowId,
			label,
			valuesByPeriod,
			adjustmentsByPeriod,
		}: CreateRowArgs<BalanceSheetId>) => {
			const sectionPath = parseGroupRowId(parentRowId);
			if (!sectionPath) {
				throw new Error("Cannot add a line item here");
			}
			const periods = filteredViewModel.periods.flatMap((p) => {
				const value = valuesByPeriod[p.id];
				const formula = adjustmentsByPeriod[p.id];
				const hasValue = Option.isSome(value);
				if (!hasValue && !formula) return [];
				return [
					{
						balanceSheetId: p.id,
						...(hasValue ? { value: Currency.round(value) } : {}),
						...(formula ? { formula } : {}),
					},
				];
			});
			await createBalanceSheetLineItem({ sectionPath, label, periods });
		},
		[createBalanceSheetLineItem, filteredViewModel.periods],
	);

	const normalizeAdjustment = useCallback<NormalizeAdjustment>((rowId, draft) => {
		const parsed = parseRowId(rowId);
		// Fall back to the context-free parse for rows whose section can't be
		// resolved; the server applies the authoritative section policy regardless.
		if (!parsed) return Formula.parse(draft);
		return normalizeFormulaForSection(draft, balanceSheetSectionPolicy(parsed.sectionPath));
	}, []);

	return (
		<FinancialDataGrid<BalanceSheetId>
			accountId={accountId}
			normalizeAdjustment={normalizeAdjustment}
			baseViewModel={filteredViewModel}
			mergeWithEdits={mergeBalanceSheetWithEdits}
			expandableRowIds={expandableRowIds}
			label="Balance Sheet"
			testId="balance-sheet-grid"
			emptyMessage="No balance sheet data for the selected periods."
			expandHotkeyMode="cycleDepth"
			readOnly={!isAdmin}
			viewOptions={isAdmin ? ADMIN_VIEW_OPTIONS : READ_ONLY_VIEW_OPTIONS}
			initialView={isAdmin ? ViewModeEnum.All : ViewModeEnum.GivenAllowed}
			onGivenOverride={handleGivenOverride}
			onAdjustmentOverride={handleAdjustmentOverride}
			onDeleteRow={handleDeleteRow}
			onCreateRow={handleCreateRow}
			onRenameRow={handleRenameRow}
			renderPeriodContextMenu={renderPeriodContextMenu}
		/>
	);
};
